import React, { createContext, useContext, useState, useEffect } from "react";
import api, { getCurrentMode } from "../utils/api";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const userData = await api.auth.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Error loading user:", err.message);
        localStorage.removeItem("token");
        setToken(null);
        setIsAuthenticated(false);
        setError("Session expired. Please login again.");
      }

      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Register user
  const register = async (userData) => {
    try {
      const response = await api.auth.register(userData);

      const authToken =
        getCurrentMode() === "development"
          ? `jwt_${response.token}`
          : response.token;
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setUser(response.user);
      setIsAuthenticated(true);
      setError(null);

      return {
        success: true,
        token: authToken,
        data: response.user,
      };
    } catch (err) {
      setError(err.message || "Registration failed");
      throw err;
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const response = await api.auth.login(email, password);

      const authToken =
        getCurrentMode() === "development"
          ? `jwt_${response.token}`
          : response.token;
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setUser(response.user);
      setIsAuthenticated(true);
      setError(null);

      return {
        success: true,
        token: authToken,
        data: response.user,
      };
    } catch (err) {
      setError(err.message || "Login failed");
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      // Simulate network delay for sandbox mode
      if (getCurrentMode() === "sandbox") {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        return { success: true, data: updatedUser };
      } else {
        // TODO: Implement real API call for profile update
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        return { success: true, data: updatedUser };
      }
    } catch (err) {
      setError(err.message || "Profile update failed");
      throw err;
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Simulate network delay for sandbox mode
      if (getCurrentMode() === "sandbox") {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return { success: true, message: "Password changed successfully" };
      } else {
        // TODO: Implement real API call for password change
        return { success: true, message: "Password changed successfully" };
      }
    } catch (err) {
      setError(err.message || "Password change failed");
      throw err;
    }
  };

  // Clear error
  const clearError = () => setError(null);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    currentMode: getCurrentMode(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
